




































































































































































































import {
  getGeoEncodingAccess,
  getTrackingAccess,
  getVehicleManagementAccess,
  getSchedulingAccess,
  getAllDevicesManagementAccess,
  getShowDeviceManagementInCockpitAccess,
  getTimeTrackingAccess,
  getDeviceCheckAccess,
  getAiPredictionsAccess,
  getRenoActivationScopeAccess,
  getBillingCatalogsAccess,
  getPersonalChatAccess,
  getProjectChatAccess,
  getMiraModeAccess,
} from "@/data/repository/ClientRepository";
import { updateSystemConfiguration } from "@/data/repository/SystemConfigurationRepository"
import {
  updateReportSettings,
} from "@/data/repository/SettingsRepository"
import { Component, Watch } from "vue-property-decorator";
import PickerDialogComponent from "../common/PickerDialog.vue";
import ClickableTextFieldComponent from "@/components/common/ClickableTextField.vue";
import { defineOwnSystemConfigurationClientAccess } from "@/permissions/ClientPermissions";
import { SystemConfigurationModule } from "@/store/modules/SystemConfigurationModule";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import { createDefaultSystemConfiguration, RenoActivationScope, SystemConfiguration } from "@/data/models/Client";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ChatMode, CHAT_MODE_VALUES, chatFlagsFromMode, chatModeFromFlags } from "@/utils/ChatMode";
import { MiraMode } from "@/data/models/MiraMode";
import { MIRA_MODE_VALUES, systemConfigurationSaveFeedback } from "@/utils/ClientSystemConfiguration";
import { AssistActivationOutcome } from "@/data/models/ClientAdministrationRow";

/**
 * Combined ASSIST selection presented in a single picker. It bundles the
 * enable flag and the one-time activation backfill scope so the scope is
 * collected when ASSIST is enabled, instead of via a separate save-time dialog.
 * `OFF` means ASSIST disabled; the other two map to {@link RenoActivationScope}.
 */
enum AiPredictionsMode {
  OFF = "OFF",
  ALL_DRYINGS = "ALL_DRYINGS",
  ACTIVE_AND_NEW_ONLY = "ACTIVE_AND_NEW_ONLY",
}

@Component({
  components: {
    "clickable-text-field": ClickableTextFieldComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class SystemConfigurationComponent extends SettingsPageMixin {
  isInitializing = true;

  enableGeoEncoding = false;
  enableTracking = false;
  enableVehicleManagement = false;
  enableScheduling = false;
  enableAllDevicesManagement = false;
  enableDeviceManagementTile = false;
  enableTimeTracking = false;
  enableDeviceCheckManagement = false;
  enableAiPredictions = false;
  enableBillingCatalogs = false;
  enablePersonalChat = false;
  enableProjectChat = false;
  renoActivationScope: RenoActivationScope = RenoActivationScope.ACTIVE_AND_NEW_ONLY;
  miraMode: MiraMode = "IRES_ONLY";

  yesNoValues = [true, false];
  aiPredictionsModeValues = [
    AiPredictionsMode.ALL_DRYINGS,
    AiPredictionsMode.ACTIVE_AND_NEW_ONLY,
    AiPredictionsMode.OFF,
  ];
  chatModeValues = CHAT_MODE_VALUES;
  miraModeValues = MIRA_MODE_VALUES;

  initialSystemConfiguration: SystemConfiguration = createDefaultSystemConfiguration();
  // IRES Cloud's answer after a save (#2610141), for the one snackbar; null before.
  assistActivation: AssistActivationOutcome = null;
  effectsFailed = false;

  @Watch("systemConfiguration")
  onSystemConfigurationChanged() {
    if (this.systemConfiguration && !this.systemConfiguration.isDefaultSystemConfiguration) {
      this.initializeParameters(this.systemConfiguration);  
    }
  }

  get hasChanged() {
    return (
      this.enableAllDevicesManagement !== this.initialSystemConfiguration.enableAllDevicesManagement ||
      this.enableDeviceManagementTile !== this.initialSystemConfiguration.enableDeviceManagementTile ||
      this.enableGeoEncoding !== this.initialSystemConfiguration.enableGeoEncoding ||
      this.enableTracking !== (this.initialSystemConfiguration.enableTracking ?? false) ||
      this.enableScheduling !== this.initialSystemConfiguration.enableScheduling ||
      this.enableTimeTracking !== this.initialSystemConfiguration.enableTimeTracking ||
      this.enableVehicleManagement !== this.initialSystemConfiguration.enableVehicleManagement ||
      this.enableDeviceCheckManagement !== this.initialSystemConfiguration.enableDeviceCheckManagement ||
      this.enableAiPredictions !== this.initialSystemConfiguration.enableAiPredictions ||
      this.enableBillingCatalogs !== this.initialSystemConfiguration.enableBillingCatalogs ||
      this.renoActivationScope !== this.initialSystemConfiguration.renoActivationScope ||
      this.enablePersonalChat !== (this.initialSystemConfiguration.enablePersonalChat ?? false) ||
      this.enableProjectChat !== (this.initialSystemConfiguration.enableProjectChat ?? false) ||
      this.miraMode !== (this.initialSystemConfiguration.miraMode ?? "IRES_ONLY")
    );
  }

  get formatMiraMode() {
    return (value: MiraMode) => {
      return this.$t(`settings.system.mira.mode.${value}`).toString();
    };
  }

  get systemConfiguration() {
    return SystemConfigurationModule.systemConfiguration;
  }

  get formatYesNo() {
    return (value: boolean) => {
      return this.$t(`common.${value ? "yes" : "no"}`).toString();
    };
  }

  /**
   * Two-way binding for the ASSIST picker. Reads the current enable flag +
   * scope into a single mode, and writes both back on selection. Selecting a
   * "Ja" option sets the activation scope directly; selecting OFF only disables
   * ASSIST and leaves the scope untouched (it is irrelevant while disabled).
   */
  get aiPredictionsMode(): AiPredictionsMode {
    if (!this.enableAiPredictions) {
      return AiPredictionsMode.OFF;
    }

    return this.renoActivationScope === RenoActivationScope.ALL_DRYINGS
      ? AiPredictionsMode.ALL_DRYINGS
      : AiPredictionsMode.ACTIVE_AND_NEW_ONLY;
  }

  set aiPredictionsMode(value: AiPredictionsMode) {
    if (value === AiPredictionsMode.OFF) {
      this.enableAiPredictions = false;
      return;
    }

    this.enableAiPredictions = true;
    this.renoActivationScope = value === AiPredictionsMode.ALL_DRYINGS
      ? RenoActivationScope.ALL_DRYINGS
      : RenoActivationScope.ACTIVE_AND_NEW_ONLY;
  }

  get formatAiPredictionsMode() {
    return (value: AiPredictionsMode) => {
      return this.$t(`settings.aiPredictions.mode.${value}`).toString();
    };
  }
  
  /**
   * Two-way binding for the chat picker: one choice on screen, two stored flags
   * (`enablePersonalChat`, `enableProjectChat`). See `src/utils/ChatMode.ts`.
   */
  get chatMode(): ChatMode {
    return chatModeFromFlags(this.enablePersonalChat, this.enableProjectChat);
  }

  set chatMode(value: ChatMode) {
    const flags = chatFlagsFromMode(value);

    this.enablePersonalChat = flags.enablePersonalChat;
    this.enableProjectChat = flags.enableProjectChat;
  }

  get formatChatMode() {
    return (value: ChatMode) => {
      return this.$t(`settings.system.chat.mode.${value}`).toString();
    };
  }

  get canEdit() {
    return defineOwnSystemConfigurationClientAccess();
  }

  get readonly() {
    return !this.isEditMode || !this.canEdit;
  }

  initializeParameters(systemConfiguration: SystemConfiguration) {
    this.enableGeoEncoding = systemConfiguration.enableGeoEncoding;
    this.enableTracking = systemConfiguration.enableTracking ?? false;
    this.enableVehicleManagement = systemConfiguration.enableVehicleManagement;
    this.enableScheduling = systemConfiguration.enableScheduling;
    this.enableAllDevicesManagement = systemConfiguration.enableAllDevicesManagement;
    this.enableDeviceManagementTile = systemConfiguration.enableDeviceManagementTile;
    this.enableTimeTracking = systemConfiguration.enableTimeTracking;
    this.enableDeviceCheckManagement = systemConfiguration.enableDeviceCheckManagement ?? false;
    this.enableAiPredictions = systemConfiguration.enableAiPredictions ?? false;
    this.enableBillingCatalogs = systemConfiguration.enableBillingCatalogs ?? false;
    this.renoActivationScope = systemConfiguration.renoActivationScope ?? RenoActivationScope.ACTIVE_AND_NEW_ONLY;
    this.enablePersonalChat = systemConfiguration.enablePersonalChat ?? false;
    this.enableProjectChat = systemConfiguration.enableProjectChat ?? false;
    this.miraMode = systemConfiguration.miraMode ?? "IRES_ONLY";

    this.isEditMode = false;
  }

  created() {
    this.isInitializing = true;

    this.initialSystemConfiguration = {
      enableGeoEncoding: getGeoEncodingAccess(),
      enableTracking: getTrackingAccess(),
      enableVehicleManagement: getVehicleManagementAccess(),
      enableScheduling: getSchedulingAccess(),
      enableAllDevicesManagement: getAllDevicesManagementAccess(),
      enableDeviceManagementTile: getShowDeviceManagementInCockpitAccess(),
      enableTimeTracking: getTimeTrackingAccess(),
      enableDeviceCheckManagement: getDeviceCheckAccess(),
      enableAiPredictions: getAiPredictionsAccess(),
      enableBillingCatalogs: getBillingCatalogsAccess(),
      renoActivationScope: getRenoActivationScopeAccess(),
      enablePersonalChat: getPersonalChatAccess(),
      enableProjectChat: getProjectChatAccess(),
      miraMode: getMiraModeAccess(),
    }
    this.initializeParameters(this.initialSystemConfiguration);

    this.isInitializing = false;
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.system.systemConfiguration").toString());
  }

  async onSave() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      return;
    }

    if (!this.hasChanged) {
      return;
    }

    this.isLoading = true;

    try {
      await this.doSave();
    } catch {
      this.isLoading = false;
      return;
    }

    this.showSaveResult();
    this.isLoading = false;
    this.$router.back();
  }

  /**
   * Saves through IRES Cloud, which since #2610141 also runs what a change sets off: the devices taken
   * out of their vehicles when Fahrzeuge goes off, Adressen geocodieren off with KARTE, and ASSIST's
   * activation when ASSIST goes on, the same as for the IRES Admin Client's Systemkonfiguration card.
   */
  async doSave() {
    this.assistActivation = null;
    this.effectsFailed = false;

    const update: SystemConfiguration = {
      enableGeoEncoding: this.enableGeoEncoding,
      enableTracking: this.enableGeoEncoding && this.enableTracking,
      enableVehicleManagement: this.enableVehicleManagement,
      enableScheduling: this.enableScheduling,
      enableAllDevicesManagement: this.enableAllDevicesManagement,
      enableDeviceManagementTile: this.enableAllDevicesManagement && this.enableDeviceManagementTile,
      enableTimeTracking: this.enableTimeTracking,
      enableDeviceCheckManagement: this.enableDeviceCheckManagement,
      enableAiPredictions: this.enableAiPredictions,
      enableBillingCatalogs: this.enableBillingCatalogs,
      renoActivationScope: this.renoActivationScope,
      enablePersonalChat: this.enablePersonalChat,
      enableProjectChat: this.enableProjectChat,
      miraMode: this.miraMode,
    }

    // Fahrzeuge off empties every vehicle (IRES Cloud does it), so it asks first.
    if (this.initialSystemConfiguration.enableVehicleManagement && !this.enableVehicleManagement) {
      const result = await this.confirmationDialogComponent.show({
        title: this.$t("settings.system.moveDevicesDialog.title").toString(),
        message: this.$t("settings.system.moveDevicesDialog.message").toString(),
      });

      if (!result) {
        throw new Error("cancelled");
      }
    }

    const save = updateSystemConfiguration(update);
    const promises: Promise<unknown>[] = [save];

    if (this.initialSystemConfiguration.enableAllDevicesManagement && !this.enableAllDevicesManagement) {
      promises.push(
        updateReportSettings({ includeGenericDeviceAssignmentsInReport: false })
      );
    }

    await Promise.all(promises);
    const saved = await save;

    this.initialSystemConfiguration = update;
    this.assistActivation = saved.assistActivation;
    this.effectsFailed = saved.effectsFailed;
  }

  private showSaveResult() {
    const feedback = systemConfigurationSaveFeedback(this.assistActivation, this.effectsFailed, "common.changesSaved");

    if (feedback.kind === "error") {
      SnackbarModule.showError({ key: feedback.key });
      return;
    }

    SnackbarModule.showSuccess({ key: feedback.key });
  }
}
